import { Controller } from "@hotwired/stimulus";

// Owns the step1 filter sidebar's own purely-cosmetic behaviour: live
// "Xh Ym" / "HH:MM - HH:MM" labels as the range sliders are dragged (input
// event, so no server round-trip per pixel -- the actual refetch is a
// separate change listener on the form itself, see
// step1_results_controller.js and _step_1_filter.html.erb). Departure/
// arrival groups aren't Stimulus targets (there's one pair per leg, and
// Stimulus's array-target indexing would be more code than just walking
// up to the closest data-role="depGroup"/"arrGroup" wrapper) -- plain DOM
// traversal from the event's own target instead.
export default class extends Controller {
  static targets = ["travelTimeInput", "travelTimeLabel"];

  connect() {
    this.updateTravelTimeLabel();
    this.element.querySelectorAll('[data-role="depGroup"]').forEach((group) => this.refreshGroupLabel(group, "dep"));
    this.element.querySelectorAll('[data-role="arrGroup"]').forEach((group) => this.refreshGroupLabel(group, "arr"));
  }

  updateTravelTimeLabel() {
    if (!this.hasTravelTimeInputTarget || !this.hasTravelTimeLabelTarget) return;
    const minutes = parseInt(this.travelTimeInputTarget.value, 10) || 0;
    const template = this.travelTimeLabelTarget.dataset.template || "%{hour}h %{min}m";
    this.travelTimeLabelTarget.textContent = template
      .replace("%{hour}", Math.floor(minutes / 60))
      .replace("%{min}", minutes % 60);
  }

  applyTravelTimeBounds(minMinutes, maxMinutes) {
    if (!this.hasTravelTimeInputTarget) return;
    this.travelTimeInputTarget.min = minMinutes;
    this.travelTimeInputTarget.max = maxMinutes;
    this.travelTimeInputTarget.value = maxMinutes;
    this.updateTravelTimeLabel();
  }

  updateDepLabel(event) {
    this.refreshGroupLabel(event.target.closest('[data-role="depGroup"]'), "dep");
  }

  updateArrLabel(event) {
    this.refreshGroupLabel(event.target.closest('[data-role="arrGroup"]'), "arr");
  }

  refreshGroupLabel(group, prefix) {
    if (!group) return;
    const min = group.querySelector(`[data-role="${prefix}Min"]`);
    const max = group.querySelector(`[data-role="${prefix}Max"]`);
    const label = group.querySelector(`[data-role="${prefix}Label"]`);
    if (!min || !max || !label) return;
    label.textContent = `${this.formatClock(min.value)} - ${this.formatClock(max.value)}`;
  }

  formatClock(totalMinutes) {
    const minutes = parseInt(totalMinutes, 10) || 0;
    // 1440 is the slider's max (end of day) -- %24 would wrap that back to
    // "00:00", displaying a full 0..1440 (unfiltered) range as the
    // zero-width-looking "00:00 - 00:00" instead of the whole day.
    if (minutes >= 1440) return "24:00";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  }

  // "all" -> children matches mobile's exact behaviour
  // (front_mobile/step_1.js.coffee, confirmed by reading it).
  toggleAllAlliances(event) {
    this.element.querySelectorAll('[data-role="alliance"]').forEach((el) => (el.checked = event.target.checked));
  }

  toggleAllAirlines(event) {
    this.element.querySelectorAll('[data-role="airline"]').forEach((el) => (el.checked = event.target.checked));
  }

  // children -> "all" (the reverse direction) is NOT what mobile does --
  // deliberately added anyway, see the long comment on the alliance
  // checkboxes in step_1_air_search_results.html.erb for why leaving "all"
  // stuck checked while an individual item is unchecked isn't just a
  // cosmetic mismatch with mobile, it makes F-ENGINE silently ignore the
  // deselection entirely (its alliance_all check counts "all" itself as a
  // key, so 6 real alliances + "all" still reads as "everything selected").
  syncAllianceAll(event) {
    this.syncAllCheckbox('allianceAll', 'alliance');
  }

  syncAirlineAll(event) {
    this.syncAllCheckbox('airlineAll', 'airline');
  }

  syncAllCheckbox(allRole, itemRole) {
    const allCheckbox = this.element.querySelector(`[data-role="${allRole}"]`);
    if (!allCheckbox) return;
    const items = this.element.querySelectorAll(`[data-role="${itemRole}"]`);
    allCheckbox.checked = Array.from(items).every((el) => el.checked);
  }
}
